/**
 * O que faz: leituras e ações da organização do Kanban no Supabase (configuração da clínica, publicação única,
 *            correção do card e painel técnico do admin).
 * Onde usa: useOrganizacaoKanban.ts e useAgentes.ts (correção na revisão).
 * Conexões: RPCs obter/publicar_organizacao_kanban, corrigir_organizacao_lead, obter_resumo_organizacao_admin,
 *           definir_limites/cortes_organizacao, registrar_privacidade_organizacao, aprovar/revogar_regra_organizacao
 *           e desativar_organizacao_kanban (reversão por clínica).
 *           Nenhuma chamada a modelo: o rascunho é montado no navegador (rascunho.ts).
 * Tabelas: indiretamente lead_classification_settings, lead_classification_column_rules, etapas_kanban, leads,
 *          lead_classification_state/route_reviews/admin_audit — sempre por RPC com autorização no servidor.
 * Data: 21/10/2026.
 */
import { supabase } from '@/integrations/supabase/client';
import type { CortesOrganizacao, FrequenciaOrganizacao, RascunhoOrganizacao, RegraColuna } from './contratos';
import type { ConfiguracaoOrganizacaoTela } from './rascunho';

// Fachada tipada mínima: os tipos gerados do Supabase ainda não cobrem as RPCs da organização.
interface RespostaBanco<T> { data: T | null; error: { message: string; code?: string } | null }
interface BancoOrganizacao {
  rpc(funcao: string, argumentos: Record<string, unknown>): PromiseLike<RespostaBanco<unknown>>;
}
const banco = supabase as unknown as BancoOrganizacao;

// SQLSTATE das nossas RPCs (permissão, conflito, validação): a mensagem já vem em português para a tela.
const CODIGOS_COM_MENSAGEM = new Set(['40001', '42501', '22023', 'P0001', 'P0002']);

/** Erro com mensagem segura para a pessoa; falhas de rede ou desconhecidas viram texto genérico. */
export class ErroOrganizacao extends Error {
  constructor(mensagem: string, readonly conflito: boolean) {
    super(mensagem);
    this.name = 'ErroOrganizacao';
  }
}

function falhar(erro: { message: string; code?: string } | null, contexto: string, padrao: string): void {
  if (!erro) return;
  // Diagnóstico fica no console; a tela só mostra frases das nossas RPCs ou o texto padrão.
  console.error(`[organizacao] ${contexto}`, erro.code, erro.message);
  const mensagem = erro.code && CODIGOS_COM_MENSAGEM.has(erro.code) ? erro.message : padrao;
  throw new ErroOrganizacao(mensagem, erro.code === '40001');
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

function paraConfiguracao(dados: unknown): ConfiguracaoOrganizacaoTela {
  const valor = (dados && typeof dados === 'object' ? dados : {}) as Record<string, unknown>;
  // Validação: resposta fora do contrato vira configuração legada vazia, nunca uma regra inventada.
  const regras = Array.isArray(valor.regras)
    ? valor.regras.filter((regra): regra is RegraColuna => Boolean(regra) && typeof regra === 'object'
      && typeof (regra as RegraColuna).etapaId === 'string' && typeof (regra as RegraColuna).descricao === 'string')
    : [];
  return {
    motor: valor.motor === 'hybrid' ? 'hybrid' : 'legacy',
    frequencia: valor.frequencia === 'quatro_horas' ? 'quatro_horas' : 'diaria',
    // bigint chega como texto e continua texto: Number poderia perder precisão em versões grandes.
    regrasVersao: typeof valor.regrasVersao === 'string' && /^\d+$/.test(valor.regrasVersao) ? valor.regrasVersao : '0',
    regras,
    calibradas: Array.isArray(valor.calibradas) ? valor.calibradas.filter((id): id is string => typeof id === 'string' && UUID.test(id)) : [],
  };
}

export async function carregarOrganizacao(clinicaId: string): Promise<ConfiguracaoOrganizacaoTela> {
  // RPC: obter_organizacao_kanban. Operação: SELECT da configuração/regras da clínica (perfil ativo, plano e tenant
  // validados no servidor). Não cria configuração nem etapas. Risco: nenhum dado de paciente.
  const { data, error } = await banco.rpc('obter_organizacao_kanban', { p_clinica_id: clinicaId });
  falhar(error, 'carregar', 'Não foi possível carregar a organização dos contatos.');
  return paraConfiguracao(data);
}

export async function publicarOrganizacao(clinicaId: string, rascunho: RascunhoOrganizacao): Promise<{
  configuracao: ConfiguracaoOrganizacaoTela; novasColunas: Record<string, string>;
}> {
  // RPC: publicar_organizacao_kanban. Operação: transação única que valida tudo, cria só as colunas sugeridas
  // ainda inexistentes (p_novas_colunas), grava as regras e liga o motor. Versão esperada impede sobrescrever
  // a publicação de outra pessoa (erro 40001). Risco: com erro, nada é gravado e o rascunho continua na tela.
  const { data, error } = await banco.rpc('publicar_organizacao_kanban', {
    p_clinica_id: clinicaId,
    p_versao_esperada: rascunho.versaoEsperada,
    p_frequencia: rascunho.frequencia,
    p_regras: rascunho.regras,
    p_novas_colunas: rascunho.novasColunas,
  });
  falhar(error, 'publicar', 'Verifique sua conexão e tente de novo.');
  const mapaBruto = (data && typeof data === 'object' ? (data as Record<string, unknown>).novasColunas : null) ?? {};
  const novasColunas = Object.fromEntries(Object.entries(mapaBruto as Record<string, unknown>)
    .filter((par): par is [string, string] => typeof par[1] === 'string' && UUID.test(par[1])));
  return { configuracao: paraConfiguracao(data), novasColunas };
}

export async function corrigirOrganizacaoLead(clinicaId: string, leadId: string, etapaId: string, etapaAlteradaEm: string | null): Promise<void> {
  // RPC: corrigir_organizacao_lead. Operação: move o card como pessoa, pausa a organização SÓ deste lead e grava a
  // amostra de calibração. A data esperada do card impede corrigir sobre uma mudança que a pessoa não viu (40001).
  const { error } = await banco.rpc('corrigir_organizacao_lead', {
    p_clinica_id: clinicaId, p_lead_id: leadId, p_etapa_id: etapaId, p_expected_changed_at: etapaAlteradaEm,
  });
  falhar(error, 'corrigir', 'Não foi possível corrigir este card. Tente novamente.');
}

// ── Painel técnico do admin ─────────────────────────────────────────────────

export interface CalibracaoRegra {
  etapaId: string;
  versao: string;
  amostras: number;
  acertos: number;
  precisao: number | null;
  falsosEventos: number;
  elegivel: boolean;
}

export interface RegraResumoAdmin {
  etapaId: string;
  nome: string;
  versao: string;
  modo: 'suggestion' | 'automatic';
  condicao: string;
  aprovadaVigente: boolean;
  calibracao: CalibracaoRegra | null;
}

export interface UsoFuncao { chamadas: number; custoUsd: number | null }

export interface ResumoOrganizacaoAdmin {
  motor: 'legacy' | 'hybrid';
  frequencia: FrequenciaOrganizacao;
  proximaJanela: string | null;
  privacidade: { validada: boolean; validadaEm: string | null; referencia: string | null; modelos: string | null };
  limites: { jev: number; llm: number; usd: number };
  cortes: CortesOrganizacao;
  modelos: { jev: string | null; llm: string | null; legado: string | null };
  usoHoje: { cotas: { jev: number; llm: number }; porFuncao: Record<string, UsoFuncao>; custoUsd: number | null };
  fila: { pendentes: number; vencidaMaisAntiga: string | null; emProcessamento: number; comFalha: number };
  regras: RegraResumoAdmin[];
}

const numero = (valor: unknown, padrao = 0) => (valor == null || Number.isNaN(Number(valor)) ? padrao : Number(valor));
const numeroOuNulo = (valor: unknown) => (valor == null || Number.isNaN(Number(valor)) ? null : Number(valor));
const textoOuNulo = (valor: unknown) => (typeof valor === 'string' ? valor : null);

function paraCalibracao(valor: unknown): CalibracaoRegra | null {
  if (!valor || typeof valor !== 'object') return null;
  const item = valor as Record<string, unknown>;
  return {
    etapaId: String(item.etapaId), versao: String(item.versao), amostras: numero(item.amostras), acertos: numero(item.acertos),
    precisao: numeroOuNulo(item.precisao), falsosEventos: numero(item.falsosEventos), elegivel: item.elegivel === true,
  };
}

// Leitura defensiva de JSON: campo ausente ou de outro tipo vira objeto vazio, sem quebrar o painel.
const objeto = (valor: unknown): Record<string, unknown> => (valor && typeof valor === 'object' && !Array.isArray(valor) ? valor as Record<string, unknown> : {});

export function paraResumoAdmin(dados: unknown): ResumoOrganizacaoAdmin {
  const valor = objeto(dados);
  const privacidade = objeto(valor.privacidade);
  const limites = objeto(valor.limites);
  const modelos = objeto(valor.modelos);
  const usoHoje = objeto(valor.usoHoje);
  const cotas = objeto(usoHoje.cotas);
  const fila = objeto(valor.fila);
  const cortes = objeto(valor.cortes);
  const porFuncao: Record<string, UsoFuncao> = {};
  for (const [funcao, uso] of Object.entries(objeto(usoHoje.porFuncao))) {
    porFuncao[funcao] = { chamadas: numero(objeto(uso).chamadas), custoUsd: numeroOuNulo(objeto(uso).custoUsd) };
  }
  return {
    motor: valor.motor === 'hybrid' ? 'hybrid' : 'legacy',
    frequencia: valor.frequencia === 'quatro_horas' ? 'quatro_horas' : 'diaria',
    proximaJanela: textoOuNulo(valor.proximaJanela),
    privacidade: {
      validada: privacidade.validada === true, validadaEm: textoOuNulo(privacidade.validadaEm),
      referencia: textoOuNulo(privacidade.referencia), modelos: textoOuNulo(privacidade.modelos),
    },
    limites: { jev: numero(limites.jev, 500), llm: numero(limites.llm, 20), usd: numero(limites.usd, 1) },
    cortes: {
      sugestao: numero(cortes.sugestao, 0.8), automatico: numero(cortes.automatico, 0.95), margem: numero(cortes.margem, 0.2),
      evidencia: numero(cortes.evidencia, 0.8), retorno: numero(cortes.retorno, 0.9),
    },
    modelos: { jev: textoOuNulo(modelos.jev), llm: textoOuNulo(modelos.llm), legado: textoOuNulo(modelos.legado) },
    usoHoje: {
      cotas: { jev: numero(cotas.jev), llm: numero(cotas.llm) },
      porFuncao,
      // Custo ausente é "desconhecido", não zero: o painel mostra isso explicitamente.
      custoUsd: numeroOuNulo(usoHoje.custoUsd),
    },
    fila: {
      pendentes: numero(fila.pendentes), vencidaMaisAntiga: textoOuNulo(fila.vencidaMaisAntiga),
      emProcessamento: numero(fila.emProcessamento), comFalha: numero(fila.comFalha),
    },
    regras: (Array.isArray(valor.regras) ? valor.regras : []).map(objeto).map((regra) => ({
      etapaId: String(regra.etapaId), nome: String(regra.nome ?? ''), versao: String(regra.versao ?? ''),
      modo: regra.modo === 'automatic' ? 'automatic' : 'suggestion', condicao: String(regra.condicao ?? ''),
      aprovadaVigente: regra.aprovadaVigente === true, calibracao: paraCalibracao(regra.calibracao),
    })),
  };
}

export async function carregarResumoAdmin(clinicaId: string): Promise<ResumoOrganizacaoAdmin> {
  // RPC: obter_resumo_organizacao_admin. Operação: agregados de uso/fila/calibração da clínica escolhida; exige admin.
  // Risco: nenhum nome, telefone ou texto de paciente volta nesta resposta.
  const { data, error } = await banco.rpc('obter_resumo_organizacao_admin', { p_clinica_id: clinicaId });
  falhar(error, 'resumo admin', 'Não foi possível carregar o painel técnico da organização.');
  return paraResumoAdmin(data);
}

export async function definirLimites(clinicaId: string, limites: { jev: number; llm: number; usd: number }): Promise<void> {
  // RPC: definir_limites_organizacao. Operação: UPDATE dos tetos diários com auditoria; zero suspende a finalidade.
  const { error } = await banco.rpc('definir_limites_organizacao', {
    p_clinica_id: clinicaId, p_limite_jev: limites.jev, p_limite_llm: limites.llm, p_limite_usd: limites.usd,
  });
  falhar(error, 'limites', 'Não foi possível salvar os limites.');
}

export async function definirCortes(clinicaId: string, cortes: CortesOrganizacao): Promise<void> {
  // RPC: definir_cortes_organizacao. Operação: UPDATE dos cortes técnicos com auditoria; revoga liberações automáticas.
  const { error } = await banco.rpc('definir_cortes_organizacao', { p_clinica_id: clinicaId, p_cortes: cortes });
  falhar(error, 'cortes', 'Não foi possível salvar os cortes técnicos.');
}

export async function registrarPrivacidade(clinicaId: string, validada: boolean, referencia: string): Promise<void> {
  // RPC: registrar_privacidade_organizacao. Operação: grava data/referência da política verificada para os modelos atuais.
  // Risco: o servidor recusa referência com formato de chave ou senha.
  const { error } = await banco.rpc('registrar_privacidade_organizacao', { p_clinica_id: clinicaId, p_validada: validada, p_referencia: referencia });
  falhar(error, 'privacidade', 'Não foi possível registrar a privacidade.');
}

export async function aprovarRegra(clinicaId: string, etapaId: string, versao: string): Promise<void> {
  // RPC: aprovar_regra_organizacao. Operação: o servidor recalcula a calibração pelos aceites/correções e só aprova
  // a versão exata com 100+ amostras, 98%+ de precisão e zero falso evento. Não liga o modo automático.
  const { error } = await banco.rpc('aprovar_regra_organizacao', { p_clinica_id: clinicaId, p_etapa_id: etapaId, p_versao: versao });
  falhar(error, 'aprovar', 'Não foi possível aprovar esta coluna.');
}

export async function desativarOrganizacao(clinicaId: string): Promise<void> {
  // RPC: desativar_organizacao_kanban. Operação: volta a clínica ao motor legado, revoga liberações automáticas e
  // tira da revisão as sugestões do motor desligado. Regras, resultados, correções e amostras ficam guardados.
  const { error } = await banco.rpc('desativar_organizacao_kanban', { p_clinica_id: clinicaId });
  falhar(error, 'desativar', 'Não foi possível voltar esta clínica ao motor legado.');
}

export async function revogarRegra(clinicaId: string, etapaId: string): Promise<void> {
  // RPC: revogar_regra_organizacao. Operação: volta a coluna para sugestão e remove a liberação, com auditoria.
  const { error } = await banco.rpc('revogar_regra_organizacao', { p_clinica_id: clinicaId, p_etapa_id: etapaId });
  falhar(error, 'revogar', 'Não foi possível revogar esta liberação.');
}
